/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import moment from 'moment';
import { DownloadIcon, SearchIcon } from '../../../assets/icons';
import { formatAmount, fromStorage, getUpdatedState } from '../../../utils';
import { Input, Render } from '../../../components';
import { ReportService } from '../../../services';

const ReportCommissions = () => {
  const { data, filters, onChange, downloadAll, downloadOne, reload, search } = useFetchTable();

  return (
    <main className="w-full">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex flex-1 flex-wrap items-end gap-3">
          <Input
            className={{ base: 'max-w-60' }}
            placeholder="Buscar placa"
            value={filters.search}
            onValueChange={v => onChange('search', v)}
            prepend={
              <img src={SearchIcon} className="w-4 h-4" alt="" />
            }
          />

          <Input
            className={{ base: 'max-w-44' }}
            type="date"
            label="Desde"
            value={filters.since}
            onValueChange={v => onChange('since', v)}
          />

          <Input
            className={{ base: 'max-w-44' }}
            type="date"
            label="Hasta"
            value={filters.until}
            onValueChange={v => onChange('until', v)}
          />

          <div className="flex gap-4">
            <button
              className="bg-red hover:bg-red-700 text-white px-6 py-2 rounded-full text-sm font-medium transition-colors"
              onClick={() => search()}
            >
              Filtrar
            </button>
            <button
              className="bg-black hover:bg-gray-800 text-white px-6 py-2 rounded-full text-sm font-medium transition-colors"
              onClick={() => reload()}
            >
              Limpiar
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-md overflow-hidden mt-6">
        <div className="flex items-center justify-between py-4 px-3">
          <p className="text-2xl font-bold text-center">Historial de comisiones</p>

          <button
            className="flex items-center gap-2 bg-black text-white px-4 py-1.5 rounded-lg font-semibold"
            onClick={() => toast.promise(downloadAll(), { loading: 'Descargando...', success: 'Reporte descargado con éxito', error: 'Error al descargar el reporte' })}
          >
            <img src={DownloadIcon} className="w-4 h-4 brightness-200" alt="" />
            Descargar
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="text-left border-collapse w-full">
            <thead>
              <tr className="border-b border-gray5 [&>th]:px-1 [&>th]:py-2 [&>th]:break-words [&>th]:max-w-36 text-default font-semibold align-bottom whitespace-pre-wrap">
                <th>Placa vehículo</th>
                <th>Fecha{'\n'}y hora</th>
                <th>Total Ingresos{'\n'}generados</th>
                <th>Total Comisión{'\n'}Cobrada ($)</th>
                <th>Ruta</th>
                <th>Ingresos{'\n'}generados (Bs)</th>
                <th>Ingresos{'\n'}generados ($)</th>
                <th>Comisión (Bs)</th>
                <th>Comisión ($)</th>
                <th align="right">Acción</th>
              </tr>
            </thead>
            <tbody className="text-sm text-text">
              {data.map((item, index) => (
                <RowItem
                  key={index}
                  index={index}
                  item={item}
                  prevItem={data.at(index - 1)}
                  onDownload={() => {
                    toast.promise(
                      downloadOne(item?.order_id),
                      {
                        loading: 'Descargando...',
                        success: 'Reporte impreso con éxito',
                        error: 'Error al imprimir el reporte',
                      }
                    );
                  }}
                />
              ))}
            </tbody>
          </table>

          {!data.length && (
            <p className="py-8 text-text text-center">No se han encontrado registros</p>
          )}
        </div>
      </div>
    </main>
  );
};

const RowItem = ({ item, prevItem, index, onDownload }) => {
  const totals = useMemo(() => {
    const totals = {
      trips: 0,
      date: '',
      time: '',
      total_in: 0,
      total_in_local: 0,
      total_paid: 0,
    };

    if (!item.isFirstOfGroup) return totals;

    totals.trips = item.group.length;
    totals.date = moment(item?.payment?.created_at).format('DD/MM/YYYY');
    totals.time = moment(item?.payment?.created_at).format('hh:mm a');
    totals.total_in = item.group.reduce((acc, x) => acc + Number(x.amount_client), 0);
    totals.total_in_local = item.group.reduce((acc, x) => acc + Number(x.amount_local_client), 0);
    totals.total_paid = item.group.reduce((acc, x) => acc + Number(x.amount), 0);

    totals.total_in = formatAmount(totals.total_in);
    totals.total_in_local = formatAmount(totals.total_in_local, 'Bs. ');
    totals.total_paid = formatAmount(totals.total_paid);

    return totals;
  }, [item]);

  return (
    <tr key={index} className="[&>td]:py-3 [&>td:nth-child(n+5)]:bg-gray5 [&>td]:px-1 [&>td]:truncate [&>td]:max-w-36 h-20 border-b-4 border-white">
      <Render condition={item.isFirstOfGroup}>
        <td>
          <p className="text-lg font-semibold text-orange mb-2">
            { item?.order?.vehicle?.plaque }
          </p>
          <span className="bg-[#000] font-semibold text-white px-2 py-1 rounded-full">
            Total de viaje { totals.trips }
          </span>
        </td>
        <td className="font-semibold text-base">
          <p>{ totals.date }</p>
          <p>{ totals.time }</p>
        </td>
        <td>
          <p className="font-black">{ totals.total_in_local }</p>
          <p className="font-semibold text-default">{ totals.total_in }</p>
        </td>
        <td>{ totals.total_paid }</td>
      </Render>

      <Render condition={!item.isFirstOfGroup}>
        <td />
        <td />
        <td />
        <td />
      </Render>

      <td>{ item?.order?.route?.name }</td>
      <td>{ formatAmount(item?.amount_local_client) }</td>
      <td>{ formatAmount(item?.amount_client) }</td>
      <td>{ formatAmount(item?.amount_local) }</td>
      <td>{ formatAmount(item?.amount) }</td>
      <td>
        <img
          src={DownloadIcon}
          className="w-5 h-5 object-contain cursor-pointer"
          onClick={onDownload}
          alt=""
        />
      </td>
    </tr>
  )
}

const useFetchTable = () => {
  const initialFilters = {
    search: '',
    since: '',
    until: '',
  };

  const [data, setData] = useState([]);
  const [canFetch, setCanFetch] = useState(true);
  const [filters, setFilters] = useState(initialFilters);

  const fetchData = async () => {
    if (!canFetch) return;
    setCanFetch(false);

    try {
      const payload = await getUpdatedState(setFilters);
      const response = await ReportService.owner.commissions.findAll(payload);

      const grouped = response.data.map((x, index) => {
        const isFirst = index === 0;
        const prevItem = response.data.at(index - 1);
        const isSameVehicle = x?.order?.vehicle_id === prevItem?.order?.vehicle_id;
        const isSamePayment = x?.payment_id === prevItem?.payment_id;
        const isFirstOfGroup = isFirst || !isSameVehicle || !isSamePayment;
        let group = [];

        if (isFirstOfGroup) {
          group = response.data.filter(y => y?.order?.vehicle_id === x?.order?.vehicle_id && y?.payment_id === x?.payment_id);
        }

        return {
          ...x,
          group,
          isFirstOfGroup,
        }
      });

      setData(grouped);
      setCanFetch(true);

    } catch (error) {
      setData([]);
      setCanFetch(true);
    }
  }

  const downloadAll = async () => {
    try {
      const response = await ReportService.owner.commissions.downloadAll(filters);
      if (response?.url) {
        const link = document.createElement('a');
        link.href = fromStorage(response.url);
        link.target = '_blank';
        link.download = response.url.split('/').pop();
        document.body.appendChild(link);
        link.click();
        link.remove();
      }

    } catch (error) {
      console.log(error);
    }
  }

  const downloadOne = async (id) => {
    try {
      const response = await ReportService.owner.commissions.downloadOne(id);
      if (response?.url) {
        const link = document.createElement('a');
        link.href = fromStorage(response.url);
        link.target = '_blank';
        link.download = response.url.split('/').pop();
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      console.log(error);
    }
  }

  const reload = (inSamePage = false) => {
    setCanFetch(true);
    if (!inSamePage) setFilters(initialFilters);
    fetchData();
  }

  const onChange = (target, value) => {
    setFilters(s => ({ ...s, [target]: value }));
  }

  useEffect(() => {
    fetchData();
  }, []);

  return {
    data,
    downloadAll,
    downloadOne,
    filters,
    isLoading: !canFetch,
    onChange,
    reload,
    search: fetchData,
  }
}

export default ReportCommissions;